import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../../modalright.css';
import '../../popup.css';
import '../../pricing-strategies.css';
import '../../wallet.css';
import '../../wallet-fixes.css';
import { getDriverApiBaseUrl } from '../../utils/utils';
import {
    formatMoney, formatInt, formatDateTime,
    Pill, StatCard, EmptyState, WalletSpinner,
    useWalletToasts, WalletToastStack, SignaturePad,
} from './walletShared';

export default function AccountantWallet() {
    const API_BASE = `${getDriverApiBaseUrl()}/shypvdriverapp/`;
    const email = sessionStorage.getItem('useremail') || '';
    const authHeaders = () => {
        const t = sessionStorage.getItem('usertoken');
        return t ? { Authorization: `Bearer ${t}` } : {};
    };
    const { toasts, toast, dismiss } = useWalletToasts();
    const navigate = useNavigate();

    const [wallet, setWallet] = useState(null);
    const [txns, setTxns] = useState([]);
    const [loading, setLoading] = useState(true);

    // Settle-to-company modal
    const [modal, setModal] = useState(false);
    const [notes, setNotes] = useState('');
    const [signature, setSignature] = useState('');
    const [saving, setSaving] = useState(false);

    const [sigView, setSigView] = useState(null);

    const money = (v) => (v != null ? Number(v).toFixed(2) : '0.00');

    const loadAll = async () => {
        setLoading(true);
        try {
            const [w, t] = await Promise.all([
                axios.get(`${API_BASE}admin/wallet/supervisor/balance`, { params: { email }, headers: authHeaders() }),
                axios.get(`${API_BASE}admin/wallet/supervisor/transactions`, { params: { email }, headers: authHeaders() }),
            ]);
            setWallet(w.data && w.data.data);
            setTxns((t.data && t.data.data) || []);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load accountant wallet.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

    const held = wallet ? Number(wallet.held || 0) : 0;
    const currency = (wallet && wallet.currency) || 'AED';

    const txnMeta = (type) => {
        switch (type) {
            case 'RECEIVED_FROM_SUPERVISOR': return { label: 'Received from supervisor', tone: 'warning', icon: 'fa-arrow-down' };
            case 'HANDOVER_TO_COMPANY': return { label: 'Settled to company', tone: 'primary', icon: 'fa-university' };
            case 'COLLECTED_FROM_DRIVER': return { label: 'Collected', tone: 'warning', icon: 'fa-arrow-down' };
            default: return { label: type || '-', tone: 'default', icon: 'fa-exchange' };
        }
    };

    const openSettle = () => { setNotes(''); setSignature(''); setModal(true); };
    const submitSettle = async () => {
        const amt = held;
        if (!(amt > 0)) return toast.error('No cash held to settle.');
        if (!signature) return toast.error('Please sign to confirm the settlement.');
        setSaving(true);
        try {
            const res = await axios.post(`${API_BASE}admin/wallet/accountant/handover`,
                { amount: amt, notes, email, signature },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
            if (res.data && res.data.success) {
                toast.success(`Settled ${formatMoney(amt, currency)} to the company.`);
                setModal(false);
                loadAll();
            } else {
                toast.error((res.data && res.data.errors && res.data.errors.map(e => e.errorMessage || e).join(', ')) || 'Failed to settle.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to submit settlement.');
        } finally { setSaving(false); }
    };

    return (
        <div className="wallet-page wui">
            <WalletToastStack toasts={toasts} onDismiss={dismiss} />

            <div className="w-topbar">
                <div className="w-topbar-left">
                    <div className="w-icon-badge"><i className="fa fa-calculator" aria-hidden="true" /></div>
                    <div>
                        <h2 className="w-title">Accountant Wallet</h2>
                        <p className="w-subtitle">
                            Cash you received from supervisors, pending settlement to the company. Signed in as <strong>{email || 'unknown'}</strong>.
                        </p>
                    </div>
                </div>
                <div className="w-topbar-actions">
                    <button className="w-btn w-btn-primary" onClick={() => navigate('/orders/awb-custody?tab=from-supervisor')}>
                        <i className="fa fa-user-secret" aria-hidden="true" /> Collect from supervisor (by AWB)
                    </button>
                    <button className="w-btn w-btn-ghost" onClick={() => navigate('/orders/awb-custody?tab=to-company')}>
                        <i className="fa fa-university" aria-hidden="true" /> Deposit to company (by AWB)
                    </button>
                    <button className="w-btn w-btn-ghost" onClick={loadAll} disabled={loading}>
                        <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`} aria-hidden="true" /> Refresh
                    </button>
                </div>
            </div>

            <div className="w-stat-grid">
                <StatCard
                    icon="fa-lock" label="Cash You Are Holding" value={`${held.toFixed(2)} ${currency}`} accent
                    action={<button className="w-btn w-btn-light w-btn-sm" onClick={() => navigate('/orders/awb-custody?tab=to-company')}>
                        <i className="fa fa-university" aria-hidden="true" /> Deposit to company (by AWB)
                    </button>}
                />
                <StatCard
                    icon="fa-exchange" label="Ledger Entries" value={formatInt(txns.length)}
                    sub="Handovers received &amp; settled"
                />
            </div>

            <div className="w-card">
                <div className="w-card-header">
                    <span className="w-card-title"><i className="fa fa-exchange" aria-hidden="true" /> Handover &amp; settlement history</span>
                    <span className="w-card-meta">{formatInt(txns.length)} entries</span>
                </div>
                <div className="w-card-body">
                    {loading ? <WalletSpinner label="Loading…" /> : txns.length === 0 ? (
                        <EmptyState icon="fa-history" title="No activity yet" subtitle="Cash handed to you by supervisors will appear here." />
                    ) : (
                        <div className="w-rtable">
                            <table className="table table-sm align-middle w-rtable-table" style={{ marginTop: 4, marginBottom: 0 }}>
                                <thead><tr><th>Date</th><th>Type</th><th>From / To</th><th className="text-end">Amount</th><th className="text-end">Held After</th><th>Note</th><th>Signature</th></tr></thead>
                                <tbody>
                                    {txns.map(t => {
                                        const m = txnMeta(t.type);
                                        return (
                                            <tr key={t.id}>
                                                <td data-label="Date" style={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: 12.5 }}>{formatDateTime(t.createdAt)}</td>
                                                <td data-label="Type"><Pill tone={m.tone} icon={m.icon}>{m.label}</Pill></td>
                                                <td data-label="From / To">{t.counterpartyEmail || (t.driverId ? `Driver #${t.driverId}` : '-')}</td>
                                                <td data-label="Amount" className="text-end" style={{ fontWeight: 600 }}>{money(t.amount)} {t.currency}</td>
                                                <td data-label="Held After" className="text-end">{money(t.balanceAfter != null ? Math.abs(t.balanceAfter) : 0)}</td>
                                                <td data-label="Note" style={{ color: '#6b7280' }}>{t.description || '-'}</td>
                                                <td data-label="Signature">
                                                    {t.signature ? (
                                                        <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => setSigView(t.signature)}>
                                                            <i className="fa fa-pencil" aria-hidden="true" /> View
                                                        </button>
                                                    ) : <span style={{ color: '#adb5bd' }}>—</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Settle modal */}
            {modal && (
                <div className="modal-overlay w-modal">
                    <div className="modal-content" style={{ maxWidth: 480 }}>
                        <div className="w-modal-header">
                            <div className="w-modal-icon"><i className="fa fa-university" aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}>
                                <h3>Settle to company</h3>
                                <p>Confirm the amount you're settling to finance</p>
                            </div>
                            <button className="close-btn" onClick={() => setModal(false)}><i className="fa fa-times" aria-hidden="true" /></button>
                        </div>
                        <div className="modal-body">
                            <div className="w-summary-line">
                                <span>You are holding</span>
                                <strong>{held.toFixed(2)} {currency}</strong>
                            </div>
                            <div className="form-group">
                                <label style={{ margin: 0 }}>Amount to settle</label>
                                <div className="w-amount-input" style={{ background: '#f8fafc' }}>
                                    <span>{currency}</span>
                                    <strong style={{ padding: '6px 0' }}>{held.toFixed(2)}</strong>
                                </div>
                                <small style={{ color: '#64748b' }}>The full amount you hold is always settled — no partial amounts.</small>
                            </div>
                            <div className="form-group"><label>Notes</label><input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. bank deposit / reference" /></div>
                            <div className="form-group">
                                <label>Signature (you — settling)</label>
                                <SignaturePad onChange={setSignature} hint="Sign above to confirm you settled the cash to the company." />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="w-btn w-btn-ghost" onClick={() => setModal(false)} disabled={saving}>Cancel</button>
                            <button className="w-btn w-btn-primary" onClick={submitSettle} disabled={saving || !(held > 0)}>
                                {saving ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Submitting…</> : <><i className="fa fa-check" aria-hidden="true" /> Confirm settlement</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Signature preview */}
            {sigView && (
                <div className="modal-overlay w-modal" onClick={() => setSigView(null)}>
                    <div className="modal-content" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
                        <div className="w-modal-header">
                            <div className="w-modal-icon"><i className="fa fa-pencil" aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}><h3>Signature</h3><p>Captured at the time of the transaction</p></div>
                            <button className="close-btn" onClick={() => setSigView(null)}><i className="fa fa-times" aria-hidden="true" /></button>
                        </div>
                        <div className="modal-body" style={{ textAlign: 'center' }}>
                            <img src={sigView} alt="signature" style={{ maxWidth: '100%', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }} />
                        </div>
                        <div className="modal-footer">
                            <button className="w-btn w-btn-ghost" onClick={() => setSigView(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
